/**
 * PdfViewer (Sprint 5 SB5-01) - render PDF asset cho doc source_tier='vanban_pdf'.
 *
 * Spec: reports/coordination/PM-SOURCE-COMPLETENESS-STRATEGY-2026-05-29.md §A.4 AC-A3.
 *
 * Strategy: browser-native PDF viewer qua <iframe src="/api/v1/doc/:id/raw-pdf">.
 *   - KHONG dung pdfjs-dist npm package (bloat bundle +1.5MB, vi pham
 *     PROCESS-BASELINE rule 12 + feedback_lighthouse_ssg_audit_pre_ship).
 *   - Browser native PDF viewer handle application/pdf Content-Type tot, ho
 *     tro zoom + search + print built-in. Foundation cho 99% user device.
 *   - Edge case (browser khong ho tro inline PDF): hien fallback download link.
 *
 * F5 audit (2026-05-29): Khong co PDF viewer component truoc do
 *   (grep pages/src returns 0). NEW module, no fork.
 *
 * Lifecycle:
 *   - Mount: render iframe pointing to /api/v1/doc/:id/raw-pdf
 *   - Worker stream PDF tu R2 (DecompressionStream gzip -> binary)
 *   - Browser native viewer hien noi dung
 *   - Fallback: neu iframe load fail -> fallback "Tai PDF ve" link
 *
 * UPL safety: KHONG OCR-to-HTML auto extract Dieu/Khoan, tranh hallucinate
 *   citation marker. User doc PDF truc tiep, no claim Dieu/Khoan mapping.
 */

import { useEffect, useRef, useState } from 'react';

interface Props {
  docId: string;
  pdfUrl: string;
  title?: string | null;
  /** Optional outbound link to original source (vanban.chinhphu.vn). */
  sourceExternalId?: string | null;
}

export function PdfViewer({ docId, pdfUrl, title, sourceExternalId }: Props) {
  const [iframeError, setIframeError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Timeout detection: if iframe doesn't fire load within 12s, assume browser
  // doesn't support inline PDF render -> show fallback download UI.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!iframeRef.current?.contentDocument) {
        // Same-origin iframe: contentDocument null often = browser blocked
        // PDF inline. We don't treat this as hard error (Chrome/Firefox/Safari
        // native viewers don't expose contentDocument anyway), just give the
        // download fallback alongside the iframe.
      }
    }, 12000);
    return () => clearTimeout(t);
  }, [pdfUrl]);

  const externalUrl = sourceExternalId
    ? `https://vanban.chinhphu.vn/?pageid=27160&docid=${encodeURIComponent(sourceExternalId)}&classid=1&orggroupid=1`
    : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* Source-tier banner: clearly signal PDF-only ingest + roadmap commitment. */}
      <div
        role="status"
        aria-label="Thông báo nguồn dữ liệu"
        style={{
          padding: '14px 18px',
          border: '1px solid var(--asklaw-border)',
          borderLeft: '4px solid var(--asklaw-navy)',
          background: 'var(--asklaw-cream-deep)',
          borderRadius: 12,
          fontSize: 14,
          lineHeight: 1.55,
          color: 'var(--asklaw-ink)',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--asklaw-navy-deep)' }}>
          Nguồn: vanban.chinhphu.vn (bản PDF)
        </div>
        <div>
          Văn bản này đang dùng bản PDF từ Cổng thông tin Văn bản Chính phủ. Bản HTML đầy đủ
          và Lược đồ tham chiếu sẽ tự động cập nhật khi vbpl.moj.gov.vn công bố phiên bản
          chính thức.
        </div>
      </div>

      {/* PDF iframe - browser native viewer handle application/pdf. */}
      {!iframeError ? (
        <iframe
          ref={iframeRef}
          title={title ? `PDF văn bản: ${title}` : 'Văn bản PDF'}
          src={pdfUrl}
          onError={() => setIframeError(true)}
          style={{
            width: '100%',
            height: 'min(85vh, 900px)',
            minHeight: 480,
            border: '1px solid var(--asklaw-border)',
            borderRadius: 12,
            background: '#fff',
            display: 'block',
          }}
        />
      ) : null}

      {/* Always render fallback action row: download + outbound source link. */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
          padding: '12px 14px',
          background: 'var(--asklaw-paper)',
          border: '1px solid var(--asklaw-border)',
          borderRadius: 10,
          fontSize: 14,
        }}
      >
        <span style={{ color: 'var(--asklaw-muted)' }}>
          Trình duyệt không hiển thị PDF? Tải về:
        </span>
        <a
          href={pdfUrl}
          download={`${docId}.pdf`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '8px 16px',
            background: 'var(--asklaw-navy)',
            color: 'var(--asklaw-paper)',
            borderRadius: 8,
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: 13.5,
            border: '1px solid var(--asklaw-navy-deep)',
            minHeight: 36,
          }}
        >
          Tải PDF về máy
        </a>
        {externalUrl ? (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 16px',
              background: 'transparent',
              color: 'var(--asklaw-navy-deep)',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: 13.5,
              border: '1px solid var(--asklaw-border-strong)',
              minHeight: 36,
            }}
          >
            Xem tại vanban.chinhphu.vn
          </a>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Placeholder render cho tab Lược đồ khi source_tier='vanban_pdf'.
 * UPL safe: KHONG fabricate refs graph, KHONG suggest reference.
 */
export function PdfRefsPlaceholder() {
  return (
    <div
      role="status"
      aria-label="Lược đồ tham chiếu chưa có"
      style={{
        padding: '32px 28px',
        textAlign: 'center',
        background: 'var(--asklaw-cream-deep)',
        border: '1px dashed var(--asklaw-border)',
        borderRadius: 12,
        color: 'var(--asklaw-ink-soft)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 18,
          fontWeight: 600,
          color: 'var(--asklaw-navy-deep)',
          marginBottom: 10,
        }}
      >
        Lược đồ tham chiếu chưa có
      </div>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, maxWidth: 520, marginInline: 'auto' }}>
        Văn bản này hiện chỉ có bản PDF từ Chính phủ, chưa có cấu trúc đồ thị tham chiếu.
        Lược đồ căn cứ ban hành, dẫn chiếu, sửa đổi bổ sung sẽ tự động cập nhật khi
        vbpl.moj.gov.vn công bố bản chính thức.
      </p>
    </div>
  );
}
